import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { pictureChoiceDimensions } from '../../../lib/picture-choice-sizes'
import { resolveSurveyMediaUrl } from '../../../lib/resolve-survey-media-url'
import type { SurveyOption, SurveyQuestion } from '../../../lib/survey-question.types'
import {
  surveyPictureChoiceCaptionSx,
  surveyPictureChoiceCardSelectedSx,
  surveyPictureChoiceCardSx,
  surveyPictureChoiceGridSx,
  surveyPictureChoiceImageSx,
  surveyPictureChoiceInvalidSx,
  surveyPictureChoiceRootSx,
} from '../../../styles/surveyExecutionStyles'

interface PictureChoiceQuestionProps {
  readonly question: SurveyQuestion
  readonly value: string | null
  readonly onChange: (optionId: string) => void
}

function PictureChoiceOptionCard({
  question,
  option,
  selected,
  dimensions,
  onSelect,
}: Readonly<{
  question: SurveyQuestion
  option: SurveyOption
  selected: boolean
  dimensions: { width: number; height: number }
  onSelect: () => void
}>) {
  const src = resolveSurveyMediaUrl(option.imageUrl)
  const label = option.label.trim()

  return (
    <Box
      component="button"
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={option.label}
      data-testid={`survey-q-${question.id}-option-${option.id}`}
      data-selected={selected ? 'true' : 'false'}
      onClick={onSelect}
      sx={{
        ...surveyPictureChoiceCardSx,
        width: dimensions.width,
        ...(selected ? surveyPictureChoiceCardSelectedSx : {}),
      }}
    >
      {src ? (
        <Box
          component="img"
          src={src}
          alt=""
          sx={{
            ...surveyPictureChoiceImageSx,
            width: dimensions.width,
            height: dimensions.height,
          }}
        />
      ) : (
        <Box
          sx={{
            ...surveyPictureChoiceImageSx,
            width: dimensions.width,
            height: dimensions.height,
          }}
          aria-hidden
        />
      )}
      {label ? (
        <Typography component="span" sx={surveyPictureChoiceCaptionSx}>
          {label}
        </Typography>
      ) : null}
    </Box>
  )
}

export function PictureChoiceQuestion({
  question,
  value,
  onChange,
}: PictureChoiceQuestionProps) {
  const withImages = question.options.filter((o) => o.imageUrl?.trim())
  const dimensions = pictureChoiceDimensions(question.imageSize)

  if (withImages.length < 2) {
    return (
      <Typography
        sx={surveyPictureChoiceInvalidSx}
        data-testid={`survey-q-${question.id}-picture-invalid`}
      >
        This picture choice question is not configured correctly.
      </Typography>
    )
  }

  return (
    <Box
      sx={surveyPictureChoiceRootSx}
      role="radiogroup"
      aria-label={question.prompt}
      data-testid={`survey-q-${question.id}-picture-choice`}
    >
      <Box sx={surveyPictureChoiceGridSx}>
        {question.options.map((opt) => (
          <PictureChoiceOptionCard
            key={opt.id}
            question={question}
            option={opt}
            selected={value === opt.id}
            dimensions={dimensions}
            onSelect={() => onChange(opt.id)}
          />
        ))}
      </Box>
    </Box>
  )
}
